<div class="card shadow">
  <!-- Title & Search input (always visible) -->
  <p class="section-title main-title">
    Describe the technology area you are interested in and we'll find the closest themes from our theme catalogue. <i class="bi bi-info-circle info-icon"
           (click)="openInfoPopup('theme_clusters')"></i>
  </p>

  <div class="d-flex align-items-top gap-2 mb-2">
    <input
      [formControl]="user_input"
      type="text"
      class="form-control"
      placeholder='Try: &quot;Composites materials for battery technologies&quot;'
      (keyup.enter)="searchClusters()">

    <div *ngIf="isSearchingClusters" class="spinner-container">
      <div class="spinner-border text-primary spinner-border-sm" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <button
      class="btn search-button"
      (click)="searchClusters()"
      [disabled]="!user_input.value?.trim() || isSearchingClusters">
      Search
    </button>
  </div>

  <!-- ============ STATE: INITIAL ============ -->
  <ng-container *ngIf="viewState === 'INITIAL'">
    <!-- Example searches -->
    <div class="shortcuts-container mb-3 mt-3" *ngIf="trendingThemes?.length">
      <p class="section-title">Example searches</p>
      <div class="shortcut-buttons">
        <button
          class="shortcut-btn"
          *ngFor="let item of trendingThemes"
          (click)="selectTrendingTheme(item)">
          {{ item.name }}
        </button>
      </div>
    </div>

    <!-- Recent Searches -->
    <div *ngIf="recentSearches.length > 0" class="recent-searches">
      <p class="section-title d-flex justify-content-between align-items-center"
         (click)="toggleRecent()">
        Recent Searches
        <span class="toggle-arrow">{{ showRecent ? '&#9650;' : '&#9660;' }}</span>
      </p>

      <div *ngIf="showRecent">
        <div class="recent-item" *ngFor="let search of recentSearches">
          <span class="search-text truncate-tooltip" (click)="setSearch(search)">
            {{ search }}
          </span>
          <span class="close-icon" (click)="removeRecent(search)">&#10005;</span>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- ============ STATE: RESULTS ============ -->
  <ng-container *ngIf="viewState === 'RESULTS'">
    <!-- Theme definer empty warning -->
    <div *ngIf="themeDefinerEmpty" class="alert alert-warning mt-2 mb-3 p-2">
      We couldn't generate a theme from this description. Additional detail can help improve the results.
    </div>

    <!-- Matching clusters -->
    <div *ngIf="clusters.length > 0" class="clusters-section mb-3">
      <div class="clusters-header">
        <span class="clusters-title">Matching Technology Clusters</span>
      </div>
      <p class="clusters-count">
        We found {{ clusters.length }} matching cluster(s) sorted by relevance.
        Select a cluster below to proceed with your search.
      </p>

      <div class="cluster-columns">
        <div class="cluster-col">
          <ng-container *ngFor="let cluster of leftColumn">
            <ng-container *ngTemplateOutlet="clusterCard; context: { $implicit: cluster }"></ng-container>
          </ng-container>
        </div>
        <div class="cluster-col">
          <ng-container *ngFor="let cluster of rightColumn">
            <ng-container *ngTemplateOutlet="clusterCard; context: { $implicit: cluster }"></ng-container>
          </ng-container>
        </div>
      </div>

      <ng-template #clusterCard let-cluster>
        <div
          class="cluster-card"
          [class.selected]="selectedCluster?.clusterid === cluster.clusterid"
          (click)="selectCluster(cluster)"
          [matTooltip]="cluster.theme_summary"
          matTooltipPosition="below"
          matTooltipClass="cluster-tooltip">
          <div class="d-flex align-items-center gap-2">
            <span class="cluster-rank">#{{ clusterRank(cluster) }}</span>
            <span class="cluster-label">{{ cluster.theme_label }}</span>
            <span class="similarity-badge ms-auto">Similarity: {{ getSimilarityPercent(cluster.similarity) }}%</span>
            <span *ngIf="selectedCluster?.clusterid === cluster.clusterid" class="selected-check">&#10003;</span>
          </div>
        </div>
      </ng-template>

      <!-- Selected cluster action panel -->
      <div *ngIf="selectedCluster" class="selected-panel mt-2">
        <p class="selected-label mb-1">Selected Cluster:</p>
        <p class="selected-name mb-2">{{ selectedCluster.theme_label }}</p>
        <button class="btn search-button w-100" (click)="searchWithCluster()">
          Search with This Cluster
        </button>
      </div>

      <!-- Theme definer fallback link -->
      <div class="fallback-link mt-2 text-end">
        <button (click)="useThemeDefiner()"
           [disabled]="isDefiningTheme"
           class="ai-definer-btn">
          <span *ngIf="isDefiningTheme" class="spinner-border spinner-border-sm me-1"></span>
          <i *ngIf="!isDefiningTheme" class="bi bi-stars ai-icon"></i>
          Or use the AI theme definer instead
        </button>
      </div>
    </div>

    <!-- No matching clusters -->
    <div *ngIf="clusters.length === 0 && !isSearchingClusters" class="no-clusters-section mb-3">
      <div class="no-clusters-header">
        <span class="clusters-title">No Matching Clusters</span>
      </div>
      <p class="no-results-message">
        We couldn't find any matching technology clusters for your search.
        Use our AI theme definer to create a custom search theme.
      </p>
      <button class="btn ai-definer-btn ai-definer-btn-block w-100"
        (click)="useThemeDefiner()"
        [disabled]="isDefiningTheme">
        <span *ngIf="isDefiningTheme" class="spinner-border spinner-border-sm me-1"></span>
        <i *ngIf="!isDefiningTheme" class="bi bi-stars ai-icon"></i>
        Use AI Theme Definer
      </button>
    </div>
  </ng-container>

  <!-- ============ STATE: THEME_DEFINER ============ -->
  <ng-container *ngIf="viewState === 'THEME_DEFINER'">
    <!-- Back link -->
    <div class="mb-2 mt-2">
      <a class="back-link" (click)="backToResults()">
        &larr; Back to search results
      </a>
    </div>

    <!-- Loading state -->
    <div *ngIf="isDefiningTheme" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Generating theme...</span>
      </div>
      <p class="mt-2 text-muted">Generating theme...</p>
    </div>

    <!-- AI-Generated Theme Query -->
    <div *ngIf="themeDefinerResult" class="definer-card mb-3">
      <div class="definer-header">
        <span class="definer-title">AI-Generated Theme Query</span>
      </div>
      <p class="generated-name mb-1">
        {{ themeDefinerResult.name }}
        <span *ngIf="isNamingTheme" class="spinner-border spinner-border-sm ms-1"></span>
      </p>
      <p class="edit-hint mb-1">
        <i class="bi bi-pencil-square"></i>
        You can edit this query below — the name updates automatically when you click away.
      </p>
      <textarea
        class="form-control generated-query mb-2"
        rows="3"
        [(ngModel)]="themeDefinerResult.query"
        [ngModelOptions]="{ standalone: true }"
        (blur)="onQueryBlur()"
        spellcheck="false"></textarea>

      <div *ngIf="queryWarnings.length" class="query-warnings mb-2">
        <div *ngFor="let w of queryWarnings" class="query-warning">
          <i class="bi bi-exclamation-triangle"></i> {{ w }}
        </div>
      </div>

      <button class="btn search-button w-100"
        (click)="searchWithThemeDefiner()"
        [disabled]="!themeDefinerResult.query.trim() || isNamingTheme">
        <span *ngIf="isNamingTheme" class="spinner-border spinner-border-sm me-1"></span>
        {{ isNamingTheme ? 'Generating name…' : 'Search with This Query' }}
      </button>
    </div>
  </ng-container>
</div>
